import {
  Box,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
} from '@chakra-ui/react'
import { IconDotsVertical as MoreVertical, IconPencil as Pencil, IconTrash as Trash2 } from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'
import { useCanHover } from '../../hooks/utility/useCanHover'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { ChatSummarySchema } from '../../types/chatTypes'
import DeleteConversationDialog from './DeleteConversationDialog'
import SidebarItem from './SidebarItem'

interface ConversationMenuAction {
  label: string
  icon: React.ReactElement
  onClick: () => void
  color?: string
}

interface ConversationRowProps {
  chat: ChatSummarySchema
  icon: React.ReactElement
  isSelected: boolean
  showLabel: boolean
  onClick: () => void
  onRename: (newTitle: string) => void
  onDelete: () => void
}

const ConversationRow: React.FC<ConversationRowProps> = ({
  chat,
  icon,
  isSelected,
  showLabel,
  onClick,
  onRename,
  onDelete,
}) => {
  const [isMenuOpen, setMenuOpen] = useState(false)
  const { t } = useLanguage()
  const canHover = useCanHover()
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { isOpen: isDeleteDialogOpen, onOpen: openDeleteDialog, onClose: closeDeleteDialog } = useDisclosure()

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.select()
    }
  }, [isEditing])

  const startEditing = () => {
    setIsEditing(true)
  }

  const commitRename = () => {
    const trimmed = inputRef.current?.value.trim() ?? ''
    if (trimmed && trimmed !== chat.title) {
      onRename(trimmed)
    }
    setIsEditing(false)
  }

  const cancelEditing = () => {
    setIsEditing(false)
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitRename()
    } else if (event.key === 'Escape') {
      cancelEditing()
    }
  }

  const menuActions: ConversationMenuAction[] = [
    {
      label: t('sidebar.rename', { defaultValue: 'Rename' }),
      icon: <Pencil size={14} />,
      onClick: startEditing,
    },
    {
      label: t('sidebar.delete', { defaultValue: 'Delete' }),
      icon: <Trash2 size={14} />,
      onClick: openDeleteDialog,
      color: 'red.500',
    },
  ]

  // In collapsed sidebar: just render a plain SidebarItem with no menu
  if (!showLabel) {
    return (
      <SidebarItem
        icon={icon}
        label={chat.title}
        onClick={onClick}
        isSelected={isSelected}
        highlightSelection
        showLabel={false}
        privacyMask={true}
      />
    )
  }

  return (
    <Box role='group' position='relative'>
      {isEditing ? (
        <Flex py={1} px={2} alignItems='center' borderRadius='md' bg='primaryScheme.50' gap={1}>
          <Input
            ref={inputRef}
            defaultValue={chat.title}
            onKeyDown={handleInputKeyDown}
            onBlur={commitRename}
            autoFocus
            size='xs'
            variant='flushed'
            flex='1'
            fontSize='sm'
            px={1}
            _focus={{ borderColor: 'primaryScheme.500' }}
          />
        </Flex>
      ) : (
        <>
          {/* Desktop titles use the whole row until its actions are revealed. */}
          <Box
            pr={canHover && !isMenuOpen ? 0 : 7}
            _groupHover={canHover ? { pr: 7 } : undefined}
            _groupFocusWithin={canHover ? { pr: 7 } : undefined}
          >
            <SidebarItem
              icon={icon}
              label={chat.title}
              onClick={onClick}
              isSelected={isSelected}
              highlightSelection
              showLabel={true}
              privacyMask={true}
            />
          </Box>
          <Menu placement='right-start' onOpen={() => setMenuOpen(true)} onClose={() => setMenuOpen(false)}>
            <MenuButton
              as={IconButton}
              aria-label={t('sidebar.conversationActions', { defaultValue: 'Conversation actions' })}
              icon={<MoreVertical size={14} />}
              size='xs'
              variant='ghost'
              position='absolute'
              right='4px'
              top='50%'
              transform='translateY(-50%)'
              // On touch the kebab is always shown. A hover-only control is
              // unreachable without a pointer anyway, and on iOS (every browser is
              // WebKit) a hover-revealed control makes the first tap on the row
              // register as a hover rather than a click, so it would need two taps.
              opacity={canHover && !isMenuOpen ? 0 : 1}
              pointerEvents={canHover && !isMenuOpen ? 'none' : 'auto'}
              _groupHover={canHover ? { opacity: 1, pointerEvents: 'auto' } : undefined}
              _groupFocusWithin={canHover ? { opacity: 1, pointerEvents: 'auto' } : undefined}
              _focusVisible={{ opacity: 1, pointerEvents: 'auto', outline: '2px solid', outlineColor: 'chat.focus' }}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            />
            <Portal>
              <MenuList minW='120px' fontSize='sm'>
                {menuActions.map(action => (
                  <MenuItem key={action.label} icon={action.icon} onClick={action.onClick} color={action.color}>
                    {action.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </>
      )}
      <DeleteConversationDialog
        isOpen={isDeleteDialogOpen}
        conversationTitle={chat.title}
        onClose={closeDeleteDialog}
        onConfirm={onDelete}
      />
    </Box>
  )
}

export default ConversationRow
